import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

export default function ProjectMap({ projectId, location }) {
  const [activeTab, setActiveTab] = useState('map')
  const [imageError, setImageError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [schemeImages, setSchemeImages] = useState([])
  const [loadingSchemes, setLoadingSchemes] = useState(true)

  const mapImageUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mapbox-static-map?projectId=${projectId}&width=600&height=300`

  useEffect(() => {
    fetchSchemeImages()
  }, [projectId])

  const fetchSchemeImages = async () => {
    try {
      setLoadingSchemes(true)
      const { data, error } = await supabase
        .from('project_images')
        .select('*')
        .eq('project_id', projectId)
        .in('image_type', ['scheme', 'floor_plan'])
        .order('image_type')
        .order('floor_level')
        .order('display_order')

      if (error) throw error

      const grouped = {}
      data?.forEach(img => {
        if (img.image_type === 'scheme') {
          if (!grouped['scheme']) grouped['scheme'] = []
          grouped['scheme'].push(img)
        } else if (img.image_type === 'floor_plan') {
          const level = img.floor_level || 'Unlabeled'
          if (!grouped[level]) grouped[level] = []
          grouped[level].push(img)
        }
      })

      setSchemeImages(grouped)
    } catch (error) {
      console.error('Error fetching scheme images:', error)
    } finally {
      setLoadingSchemes(false)
    }
  }

  const handleImageLoad = () => {
    setLoading(false)
    setImageError(false)
  }

  const handleImageError = () => {
    setLoading(false)
    setImageError(true)
  }

  const hasSchemes = Object.keys(schemeImages).length > 0

  return (
    <div className="card overflow-hidden">
      <div className="border-b border-secondary-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('map')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'map'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
            }`}
          >
            Location Map
          </button>
          <button
            onClick={() => setActiveTab('scheme')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'scheme'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
            }`}
          >
            Development Scheme
          </button>
        </div>
      </div>

      {activeTab === 'map' && (
        <>
          {imageError ? (
            <div className="p-4 sm:p-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-secondary-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-secondary-900 mb-1">Location</h3>
                  <p className="text-secondary-600">{location}</p>
                  <p className="text-sm text-secondary-500 mt-2">
                    Map not available. Please ensure the developer has configured their Mapbox API key.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="relative">
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-secondary-100">
                    <div className="spinner spinner-lg text-primary-600"></div>
                  </div>
                )}
                <img
                  src={mapImageUrl}
                  alt={`Map of ${location}`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  className={`w-full h-[300px] object-cover transition-opacity duration-200 ${loading ? 'opacity-0' : 'opacity-100'}`}
                />
              </div>
              <div className="p-4 bg-secondary-50 border-t border-secondary-200">
                <div className="flex items-center gap-2 text-secondary-700 mb-3">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium">{location}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-secondary-600">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#3b82f6]"></div>
                    <span>Project</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#f1c40f]"></div>
                    <span>Schools</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#27ae60]"></div>
                    <span>Supermarkets</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#8b4513]"></div>
                    <span>Cafes</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'scheme' && (
        <div className="p-4">
          {loadingSchemes ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner spinner-lg text-primary-600"></div>
            </div>
          ) : !hasSchemes ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 text-secondary-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-secondary-600">No development scheme available</p>
              <p className="text-sm text-secondary-500 mt-1">The developer has not uploaded scheme images yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(schemeImages).map(([level, images]) => (
                <div key={level}>
                  {level !== 'scheme' && (
                    <h3 className="text-sm font-semibold text-secondary-900 mb-3">{level}</h3>
                  )}
                  <div className="space-y-4">
                    {images.map((img) => (
                      <div key={img.id} className="border border-secondary-200 rounded-lg overflow-hidden">
                        <img
                          src={img.image_url}
                          alt={img.description || `${level} plan`}
                          className="w-full h-auto"
                        />
                        {img.description && (
                          <div className="p-3 bg-secondary-50 text-sm text-secondary-700">
                            {img.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
